/** @jsxImportSource @emotion/react */

import { Checkbox, Modal, Typography } from "@a_team/ui-components";
import TrackButton from "components/TrackButton";
import { Flags } from "configs/featureFlags";
import { useCSSRulesWithTheme } from "hooks/useCSSRulesWithTheme";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { observer } from "mobx-react";
import { FC, useState } from "react";
import {
  userAcceptTermsAndConditions,
  userAcceptV2TermsAndConditions,
} from "services/user";
import { useRootStore } from "store";
import { useSignOut } from "queries/auth/useSignOut";

const AcceptTermsModal = () => {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    authStore: auth,
    uiStore: { setToast },
    userStore,
  } = useRootStore();

  const styles = useCSSRulesWithTheme(() => ({
    container: {
      padding: 0,
    },
    header: {
      marginBottom: 4,
    },
    description: {
      marginBottom: 8,
    },
    checkBox: {
      marginBottom: 20,
    },
  }));

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await userAcceptTermsAndConditions(auth);
      await userStore.updateUser(true);
    } catch (e) {
      setToast({ text: "Unable to save", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={true} canClose={false} variant="action" zIndex={10005}>
      <div css={styles.container}>
        <Typography
          variant="textLarge"
          weight="bold"
          component="div"
          css={styles.header}
        >
          To continue, you must accept our Terms of Service
        </Typography>

        <div css={styles.description}>
          <Typography variant="textMedium">
            Please review and accept A.Team’s{" "}
            <a
              target="_blank"
              href="https://platform.a.team/client-terms-of-service"
            >
              Terms of Service
            </a>{" "}
            and{" "}
            <a target="_blank" href="https://platform.a.team/privacy-policy">
              Privacy Policy
            </a>
            .
          </Typography>
        </div>
        <div css={styles.checkBox}>
          <Checkbox
            onChange={() => setAccepted(!accepted)}
            checked={accepted}
            label="I have reviewed and accept the terms and policy."
            data-testing-id="accept-terms-of-service-checkbox"
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <TrackButton
            name="Accept terms and conditions"
            onClick={handleSubmit}
            disabled={!accepted || submitting}
            loading={submitting}
            data-testing-id="accept-terms-of-service-submit"
          >
            Submit
          </TrackButton>
        </div>
      </div>
    </Modal>
  );
};

const AcceptV2TermsModal = ({
  tosAlreadyAccepted,
}: {
  tosAlreadyAccepted: boolean;
}) => {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const {
    authStore: auth,
    uiStore: { setToast },
    userStore,
  } = useRootStore();

  const { mutate: signOut } = useSignOut();
  const styles = useCSSRulesWithTheme(() => ({
    container: {
      padding: 0,
      display: "flex",
      flexDirection: "column" as const,
      gap: "4px",
      "& button": {
        padding: "10px 24px",
      },
    },
    header: {
      marginBottom: 4,
    },
    description: {
      marginBottom: 8,
    },
    checkBox: {
      marginBottom: 20,
    },
  }));

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      await userAcceptV2TermsAndConditions(auth);
      await userStore.updateUser(true);
    } catch (e) {
      setToast({ text: "Unable to accept terms of service", type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    // should be max z-index in the app
    <Modal isOpen={true} canClose={false} variant="action" zIndex={20000}>
      <div css={styles.container}>
        <Typography
          variant="textLarge"
          weight="bold"
          component="div"
          css={styles.header}
        >
          {tosAlreadyAccepted
            ? "We've updated our Platform Terms of Service"
            : "To continue, you must accept our Terms of Service"}
        </Typography>

        <div css={styles.description}>
          <Typography variant="textMedium">
            Please review and accept{tosAlreadyAccepted ? " the updated" : ""}{" "}
            <a target="_blank" href="https://platform.a.team/terms-of-service">
              Platform Terms of Service
            </a>{" "}
            and{" "}
            <a target="_blank" href="https://platform.a.team/privacy-policy">
              Privacy Policy
            </a>{" "}
            to continue using A.Team.
          </Typography>
        </div>
        <div css={styles.checkBox}>
          <Checkbox
            onChange={() => setAccepted(!accepted)}
            checked={accepted}
            label={
              tosAlreadyAccepted
                ? "I have read and agree to the updated Platform Terms of Service."
                : "I have reviewed and accept the terms and policy."
            }
            data-testing-id="accept-terms-of-service-v2-checkbox"
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            gap: "8px",
          }}
        >
          {tosAlreadyAccepted && (
            <TrackButton
              name="Terms of Service Sign Out"
              onClick={() => signOut()}
              variant="secondary"
              size="sm"
            >
              Sign out
            </TrackButton>
          )}
          <TrackButton
            name="Accept terms and conditions"
            onClick={handleSubmit}
            disabled={!accepted || submitting}
            loading={submitting}
            size="sm"
          >
            Agree
          </TrackButton>
        </div>
      </div>
    </Modal>
  );
};

const ForcingModalProvider: FC = ({ children }) => {
  const {
    userStore: { user },
    authStore: { isLoggedIn },
  } = useRootStore();

  const { flagEnabled: isV2ContractsEnabled } = useFeatureFlag(
    Flags.ContractsV2
  );

  const showToSV1Modal = isLoggedIn && user?.uid && user?.needsAcceptTOS;
  const showToSV2Modal =
    isLoggedIn && user && user.uid && user.acceptedTOSVersion !== "v2";

  return (
    <div>
      {children}
      {!isV2ContractsEnabled && showToSV1Modal && <AcceptTermsModal />}
      {isV2ContractsEnabled && showToSV2Modal && (
        <AcceptV2TermsModal tosAlreadyAccepted={!user?.needsAcceptTOS} />
      )}
    </div>
  );
};

export default observer(ForcingModalProvider);
